import React, { useId, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEllipsis, faPencil, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import styles from './organizational-structure.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { colorPalette } from 'shared/constants';
import { IconButton, Dropdown, SearchBox } from 'src/components/design-system';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { OptionModal } from './option-modal';
import { DeleteOptionModal } from './delete-option-modal';
import {
  CHILD_FIELD_BY_FIELD,
  PARENT_FIELD_BY_FIELD,
  CHILD_COUNT_LANG,
  DEPENDENT_LANG,
} from './profile-field-relations';

/**
 * A single profile field rendered as a card: title (with count), a filter box,
 * and a scrollable option list with per-row add / edit / delete. Prefers
 * `option.label`; falls back to the raw `code`.
 *
 * Dependent fields (role, levelOfTraining) render the options of the currently
 * selected parent option; selectable fields (profession, role) let a row be
 * clicked to drive their child card.
 *
 * @param {{
 *   field: import('shared/api/profile-fields.service.api').ProfileField,
 *   allFields: import('shared/api/profile-fields.service.api').ProfileField[],
 *   stats: Record<string, Record<string, number>> | undefined,
 *   selection: Record<string, string>,
 *   setSelection: (updater: (prev: Record<string, string>) => Record<string, string>) => void,
 *   merged?: boolean,
 * }} props
 */
export function FieldCard({ field, allFields, stats, selection, setSelection, merged = false }) {
  const lang = useSelector(getLang('SETTINGS'));
  const headingId = useId();
  const [filter, setFilter] = useState('');
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const childFieldKey = CHILD_FIELD_BY_FIELD[field.fieldKey];
  const parentFieldKey = PARENT_FIELD_BY_FIELD[field.fieldKey];
  const isSelectable = !!childFieldKey;
  const isDependent = !!parentFieldKey;

  const childField = useMemo(
    () => (childFieldKey ? allFields.find(f => f.fieldKey === childFieldKey) : null),
    [allFields, childFieldKey],
  );
  // Ids of this field's options that have child options (roles under a profession, …).
  const optionIdsWithChildren = useMemo(
    () => new Set((childField?.options ?? []).map(o => o.parentOptionId).filter(Boolean)),
    [childField],
  );
  const parentField = useMemo(
    () => (parentFieldKey ? allFields.find(f => f.fieldKey === parentFieldKey) : null),
    [allFields, parentFieldKey],
  );

  const selectedParentId = isDependent ? selection[parentFieldKey] : null;
  const selectedParentOption = useMemo(
    () => parentField && parentField.options.find(o => o._id === selectedParentId),
    [parentField, selectedParentId],
  );

  // Options in scope for this card (before the text filter).
  const scopedOptions = useMemo(() => {
    const opts = field.options || [];
    if (!isDependent) return opts;
    return selectedParentId ? opts.filter(o => o.parentOptionId === selectedParentId) : [];
  }, [field.options, isDependent, selectedParentId]);

  const visibleOptions = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return scopedOptions;
    return scopedOptions.filter(o => (o.label || o.code).toLowerCase().includes(q));
  }, [scopedOptions, filter]);

  const count = scopedOptions.length;

  const titleLabel = useMemo(() => {
    if (isDependent) {
      if (!selectedParentOption) return field.displayLabel;
      return lang[DEPENDENT_LANG[field.fieldKey].title].replace(
        '{parent}',
        selectedParentOption.label || selectedParentOption.code,
      );
    }
    return field.displayLabel;
  }, [isDependent, selectedParentOption, field.displayLabel, field.fieldKey, lang]);

  const showCount = !isDependent || !!selectedParentOption;

  const addDisabled = isDependent && !selectedParentId;
  const addLabel = lang.ADD_ITEM.replace('{label}', (field.displayLabel || '').toLowerCase());
  const addTitle = addDisabled
    ? lang.ADD_SELECT_PARENT_FIRST.replace(
        '{parent}',
        (parentField?.displayLabel || parentFieldKey || '').toLowerCase(),
      )
    : addLabel;

  const openAdd = () => {
    setEditingOption(null);
    setOptionModalOpen(true);
  };

  const openEdit = option => {
    setEditingOption(option);
    setOptionModalOpen(true);
  };

  const selectRow = option => {
    if (!isSelectable) return;
    setSelection(prev => {
      const next = { ...prev };
      const isDeselect = prev[field.fieldKey] === option._id;

      for (let key = field.fieldKey; key; key = CHILD_FIELD_BY_FIELD[key]) {
        delete next[key];
      }

      if (!isDeselect) next[field.fieldKey] = option._id;
      return next;
    });
  };

  const childMeta = option => {
    if (!childFieldKey || !childField) return null;
    const n = childField.options.filter(o => o.parentOptionId === option._id).length;
    const langKeys = CHILD_COUNT_LANG[childFieldKey];
    return n > 0 ? lang[langKeys.some].replace('{count}', formatNumber(n)) : lang[langKeys.none];
  };

  const activateRow = (_option, index) => {
    const option = visibleOptions[index];
    if (option) selectRow(option);
  };

  const renderBody = () => {
    if (isDependent && !selectedParentId) {
      return <div className={styles.emptyHint}>{lang[DEPENDENT_LANG[field.fieldKey].hint]}</div>;
    }
    if (visibleOptions.length === 0) {
      return (
        <div className={styles.emptyHint}>{filter.trim() ? lang.NO_MATCHES : lang.NO_OPTIONS}</div>
      );
    }
    return (
      <ListItemsNavigator
        className={styles.optionList}
        onItemActivate={activateRow}
        aria-labelledby={headingId}>
        {visibleOptions.map(option => {
          const selected = isSelectable && selection[field.fieldKey] === option._id;
          const meta = childMeta(option);
          const menuItems = [
            {
              key: 'edit',
              icon: <FontAwesomeIcon icon={faPencil} color={colorPalette.primary} />,
              label: <span style={{ color: colorPalette.primary }}>{lang.MENU_EDIT}</span>,
              onClick: () => openEdit(option),
            },
            {
              key: 'delete',
              icon: <FontAwesomeIcon icon={faTrashCan} />,
              label: lang.MENU_DELETE,
              danger: true,
              onClick: () => setDeleteTarget(option),
            },
          ];
          return (
            <div
              key={option._id}
              className={classNames(styles.optionRow, {
                [styles.optionRowSelectable]: isSelectable,
                [styles.optionRowSelected]: selected,
              })}
              // ListItemsNavigator injects role="listitem"; aria-pressed is invalid there,
              // so express the selected (child-driving) row with aria-current instead.
              aria-current={isSelectable && selected ? 'true' : undefined}
              onClick={isSelectable ? () => selectRow(option) : undefined}>
              <span className={styles.optionText}>
                {option.label || option.code}
                {meta && <span className={styles.optionMeta}>{` • ${meta}`}</span>}
              </span>
              <Dropdown
                items={menuItems}
                trigger={['click']}
                placement="bottomRight"
                aria-label={lang.OPTION_ACTIONS}
                childrenWrapperStyle={styles.kebab}>
                <FontAwesomeIcon icon={faEllipsis} color={colorPalette.primaryMain6} />
              </Dropdown>
            </div>
          );
        })}
      </ListItemsNavigator>
    );
  };

  return (
    // role="group" + a stable aria-label (the field's displayLabel, without the
    // changing count) gives each card a unique accessible container so its
    // controls — which share aria-labels across cards ("Add …", "Options",
    // "Type to filter") — can be scoped unambiguously (e.g. by e2e tests / AT).
    <div
      className={classNames(styles.card, { [styles.cardMerged]: merged })}
      role="group"
      aria-label={field.displayLabel}>
      <div className={styles.cardHeader}>
        <h3 id={headingId} className={styles.cardTitle}>
          {titleLabel}
          {showCount && <span aria-hidden="true">{` (${formatNumber(count)})`}</span>}
        </h3>
        <IconButton
          label={addTitle}
          className={styles.addButton}
          onClick={openAdd}
          disabled={addDisabled}>
          <FontAwesomeIcon icon={faPlus} />
        </IconButton>
      </div>

      <SearchBox
        className={styles.searchBox}
        placeholder={lang.FILTER_PLACEHOLDER}
        value={filter}
        onChange={e => setFilter(e.target.value)}
        aria-label={lang.FILTER_PLACEHOLDER}
      />

      {renderBody()}

      <OptionModal
        open={optionModalOpen}
        field={field}
        option={editingOption}
        parentOptionId={selectedParentId}
        parentLabel={
          selectedParentOption ? selectedParentOption.label || selectedParentOption.code : null
        }
        onClose={() => setOptionModalOpen(false)}
      />
      <DeleteOptionModal
        open={!!deleteTarget}
        field={field}
        option={deleteTarget}
        optionIdsWithChildren={optionIdsWithChildren}
        childFieldLabel={childField ? childField.displayLabel : null}
        parentFieldLabel={parentField ? parentField.displayLabel : null}
        stats={stats}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
